/* eslint-disable arrow-body-style */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, tap, withLatestFrom, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { ConnectionService } from '@services/connection-service';
import { resetCacheByName } from '@services/connection-service/cache-interceptor';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { Params } from '@angular/router';
import { ApiClientConstant, ParseKeys, Table } from '@cureskin/api-client';
import * as Types from '../modals';
import { fromActions } from '../actions';
import { fromSelectors } from '../selectors';
import { AppConfig } from '../../app.config';
import { RegimenExpiryService } from '@services/regimen-expiry-service/regimen-expiry.service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { CheckoutService } from '@services/checkout-service';

@Injectable()
export class CheckoutEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly conn: ConnectionService,
    private readonly store: Store,
    private readonly eventLogger: EventLoggerService,
    private readonly broadcastService: BroadcastService,
    private readonly appConfig: AppConfig,
    private readonly regimenExpiryService: RegimenExpiryService,
    private readonly experimentService: ExperimentService,
    private readonly checkoutService: CheckoutService,
  ) {
  }

  getDefalutAddressBegin$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookDefaultFetchBegin),
      withLatestFrom(this.store.select(fromSelectors.selectDefaultAddress)),
      exhaustMap(async ([action, deliverHereAddress]: any[]): Promise<any> => {
        const address = deliverHereAddress || await this.conn.fetchDefaultAddress(this.conn.getActingUser());
        if (address) {
          return this.store.dispatch(fromActions.AddressBookUpdate({ payload: { defaultAddress: JSON.parse(JSON.stringify(address)) } }));
        }
        // Skip redirect for review-order flow — component handles navigation itself
        if (action.skipRedirectOnMissing) return;
        // No default address found — check if user has any addresses at all
        const allAddresses = await this.conn.fetchAllAddress(this.conn.getActingUser(), ['objectId']);
        this.store.select(fromSelectors.selectQueryParams).pipe(take(1)).subscribe((queryParams: Params): void => {
          const addressParams = this.checkoutService.buildPreservedAddressParams(queryParams, { fromCheckout: 'true' });
          if (allAddresses?.length) {
            this.conn.navigateToURL(this.checkoutService.buildUrlWithParams('/user/address', addressParams));
          } else {
            this.conn.navigateToURL(this.checkoutService.buildUrlWithParams('/user/address/new', addressParams));
          }
        });
        return;
      }),
      catchError((error: string, caught: Observable<unknown>): Observable<unknown> => {
        return caught;
      }),
    );
  }, { dispatch: false });

  getAddressBookListBegin$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookListFetchBegin),
      withLatestFrom(this.store.select(fromSelectors.selectAddressBookList)),
      exhaustMap(([{ fromCache }, addressBookList]: any[]): any => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { isFetchInProgress: true } }));
        if (addressBookList.length && fromCache) return of(addressBookList);
        return this.conn.fetchAllAddress(this.conn.getActingUser());
      }),
      tap((addressList: any[] = []): void => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { addressList: JSON.parse(JSON.stringify(addressList)),
          isFetchInProgress: false } }));
        this.store.select(fromSelectors.selectQueryParams).pipe(take(1)).subscribe((params: Params): void => {
          if (!addressList.length && !this.isShiprocketFlow(params)) {
            this.conn.navigateToURL('/user/address/new');
          }
        });
      }),
      catchError((error: string, caught: Observable<unknown>): Observable<unknown> => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { isFetchInProgress: false } }));
        return caught;
      }),
    );
  }, { dispatch: false });

  deleteAddressBegin$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookDeleteBegin),
      exhaustMap(({ address }: { address: Types.AddressBookI }): any => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { isFetchInProgress: true } }));
        const addressBook = new Table.AddressBook();
        addressBook.id = address.objectId;
        return addressBook.destroy();
      }),
      map((): void => {
        resetCacheByName('fetchDefaultAddress');
        this.broadcastService.broadcast('NOTIFY', { message: 'Address deleted successfully.' });
        this.store.dispatch(fromActions.AddressBookListFetchBegin({}));
      }),
      catchError((error: any, caught: Observable<unknown>): Observable<unknown> => {
        const message = error?.code === 412
          ? $localize`:@@cannotDeleteLastAddress:This address can't be deleted as at least one address is required. Edit it or add a new one to proceed.`
          : error?.toString();
        this.broadcastService.broadcast('NOTIFY', { message });
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { isFetchInProgress: false } }));
        return caught;
      }),
    );
  }, { dispatch: false });

  setDeliveryAddress$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookSetDeliveryAddress),
      withLatestFrom(this.store.select(fromSelectors.selectQueryParams)),
      exhaustMap(([{ deliveryAddress, skipNavigationBack }, queryParams]: [
        { deliveryAddress: Types.AddressBookI, skipNavigationBack?: boolean },
        Params,
      ]): any => {
        if (queryParams.orderId) {
          return this.store.dispatch(fromActions.AddressBookUpdateOrderAddress({ deliveryAddress, orderId: queryParams.orderId }));
        }
        if (!skipNavigationBack) {
          this.broadcastService.broadcast('NAVIGATION_BACK');
        }
        resetCacheByName('fetchDefaultAddress');
        return this.store.dispatch(fromActions.AddressBookUpdate({ payload: { defaultAddress: deliveryAddress } }));
      }),
    );
  });

  saveAddressBegin$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookSaveBegin),
      exhaustMap(async ({ address, addressId, skipNavigationBack, setAsDeliveryAddress }: {
        address: Types.AddressBookI,
        addressId: string,
        skipNavigationBack?: boolean,
        setAsDeliveryAddress?: boolean,
      }): Promise<any> => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { saveInProcess: true } }));
        const savedAddress = await this.updatedAddressBook(address, addressId);
        // Re-fetch when promoting to default — save() returns server-encrypted fields
        // (mobileNumber). fetchAddressById runs the read pipeline that decrypts them.
        const defaultAddressForStore = setAsDeliveryAddress
          ? await this.conn.fetchAddressById(savedAddress?.id || addressId)
          : null;
        return { defaultAddressForStore, skipNavigationBack, setAsDeliveryAddress };
      }),
      map(({ defaultAddressForStore, skipNavigationBack, setAsDeliveryAddress }: {
        defaultAddressForStore: Table.AddressBook | null,
        skipNavigationBack?: boolean,
        setAsDeliveryAddress?: boolean,
      }): void => {
        const payload: Partial<Types.AddressBookStateI> = { saveInProcess: false };
        if (setAsDeliveryAddress && defaultAddressForStore) {
          payload.defaultAddress = JSON.parse(JSON.stringify(defaultAddressForStore));
        }
        this.store.dispatch(fromActions.AddressBookUpdate({ payload }));
        resetCacheByName('fetchDefaultAddress');
        if (!skipNavigationBack) {
          this.broadcastService.broadcast('NAVIGATION_BACK');
        }
      }),
      catchError((error: string, caught: Observable<unknown>): Observable<unknown> => {
        this.store.dispatch(fromActions.AddressBookUpdate({ payload: { saveInProcess: false } }));
        this.broadcastService.broadcast('NOTIFY', { message: error.toString() });
        return caught;
      }),
    );
  }, { dispatch: false });

  updateOrderAddress$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.AddressBookUpdateOrderAddress),
      exhaustMap(({ orderId, deliveryAddress }: { deliveryAddress: Types.AddressBookI, orderId: string }): any => {
        return this.conn.updateOrderBeforeShipment({ id: orderId, addressBookId: deliveryAddress.objectId });
      }),
      map((): void => {
        this.broadcastService.broadcast('NOTIFY', { message: 'Delivery address changes successfully.' });
        this.broadcastService.broadcast('NAVIGATION_BACK');
      }),
      catchError((error: string, caught: Observable<unknown>): Observable<unknown> => {
        this.broadcastService.broadcast('NOTIFY', { message: error.toString() });
        this.broadcastService.broadcast('NAVIGATION_BACK');
        return caught;
      }),
    );
  }, { dispatch: false });

  checkoutOrderBegin$: any = createEffect((): any => {
    return this.actions$.pipe(
      ofType(fromActions.CheckoutOrderBegin),
      withLatestFrom(this.store.select(fromSelectors.selectDefaultAddress)),
      exhaustMap(async ([props, defaultAddress]: [any, Types.AddressBookI]): Promise<any> => {
        return this.checkoutOrder(props, defaultAddress);
      }),
      map(({ order, replaceUrl }: { order: any, replaceUrl: boolean }): any => {
        this.store.dispatch(fromActions.CheckoutOrderUpdate({ checkout: { checkoutInProgress: false } }));
        return this.conn.navigateToURL(`/user/order/${order.id}/payment`, false, replaceUrl);
      }),
      catchError((error: any, caught: Observable<unknown>): Observable<unknown> => {
        this.store.dispatch(fromActions.CheckoutOrderUpdate({ checkout: { checkoutInProgress: false } }));
        if (error.message && error.code === 409) this.showError('We have the same order in process', true);
        else this.showError(error.message, true);
        this.eventLogger.trackEvent('order_creation_failed', { Exception: error.toString() });
        return caught;
      }),
    );
  }, { dispatch: false });

  async checkoutOrder(props: { payload: any, replaceUrl: boolean }, defaultAddress: Types.AddressBookI): Promise<any> {
    const user = this.conn.getActingUser();
    this.eventLogger.trackInParse('ADDRESS_SELECTED', ApiClientConstant.Event.Type.ORDER);
    this.eventLogger.trackEvent('checkout_validation_success', { regimenId: props.payload.regimenId });
    this.eventLogger.trackInElasticSearch({
      username: user.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User checkout validation success',
      event: 'CHECKOUT_VALIDATION_SUCCESS',
      params: { regimenId: props.payload.regimenId },
    });
    this.store.dispatch(fromActions.CheckoutOrderUpdate({ checkout: { checkoutInProgress: true } }));
    if (!defaultAddress && props.payload.type !== this.appConfig.Shared.Order.Type.CONSULTATION) {
      return this.conn.navigateToURL('/user/address/new');
    }
    // Address used for order is marked as default address
    const address = await this.conn.fetchAddressById(defaultAddress?.objectId);
    if (address) {
      address.set('default', true);
      await address.save();
    }

    if (props?.payload?.regimenReorderDetails?.enabled) {
      this.eventLogger.trackInElasticSearch({
        username: user.get('username'),
        message: 'Checkout called for regimen reorder',
        event: 'CHECKOUT_REGIMEN_REORDER',
        params: {
          products: props.payload?.products,
          regimenReorderDetails: props.payload?.regimenReorderDetails,
        }
      })
    }

    // First, create/get the order
    let order = await this.conn.checkoutAndCreateOrder({ ...props.payload, addressBookId: defaultAddress?.objectId });
    // Preserve user’s useCureSkinCash preference in sessionStorage only when applyCash experiment is ON
    if (this.experimentService.applyCashExperiment) {
    const storedUseCureSkinCash = sessionStorage.getItem(`useCureSkinCash_${order.id}`);
    let useCureSkinCashToStore = props.payload.useCureSkinCash;

    // If sessionStorage has a value, use that instead of payload value (user's choice takes precedence)
    if (storedUseCureSkinCash !== null && storedUseCureSkinCash !== '') {
      const storedValue = storedUseCureSkinCash === 'true';
      // Only update order if stored value differs from payload value
      if (props.payload.useCureSkinCash !== storedValue) {
        order = await this.conn.checkoutAndCreateOrder({
          orderId: order.id,
            useCureSkinCash: storedValue,
        });
        useCureSkinCashToStore = storedValue;
      } else {
        useCureSkinCashToStore = storedValue;
      }
    }

    // Store useCureSkinCash in sessionStorage for payment page refresh
    if (useCureSkinCashToStore !== undefined) {
      sessionStorage.setItem(`useCureSkinCash_${order.id}`, String(useCureSkinCashToStore));
      }
    }
    const couponCodeDetails = this.regimenExpiryService.getCouponCodeDetails();
    if (couponCodeDetails.isCouponApplied && couponCodeDetails?.couponCode) {
      try {
        await this.conn.applyDiscountWith(couponCodeDetails?.couponCode, order?.id);
      } catch (error) {
        this.eventLogger.trackEvent('regimen_expiry_coupon_application_failed', { orderId: order?.id, error: error?.message || JSON.stringify(error), couponCode: couponCodeDetails?.couponCode });
      }
    }
    return { order, replaceUrl: props.replaceUrl };
  }

  showError(message: string, goBack?: boolean): void {
    this.broadcastService.broadcast('NOTIFY', { message });
    if (!goBack) return;
    this.broadcastService.broadcast('NAVIGATION_BACK');
  }

  private isShiprocketFlow(params: Params): boolean {
    return params?.source === this.appConfig.Shared?.Shiprocket?.Source;
  }

  async updatedAddressBook(address: Types.AddressBookI, addressId: string): Promise<Table.AddressBook> {
    const newAddressObject = new Table.AddressBook();
    newAddressObject.id = addressId;
    Object.keys(address).forEach((each: ParseKeys<Table.AddressBook>): void => {
      newAddressObject.set(each, address[each]);
    });

    newAddressObject.set('user', this.conn.getActingUser());

    return newAddressObject.save();
  }
}
